import React from 'react';
import { t, Trans } from '@lingui/macro';

import HowBrandWorks from '@rover/components/src/HowBrandWorks';
import LandingHero, {
  HeroImageSrcSet,
} from '@rover/react-lib/src/pages/landing/components/LandingHero';
import LandingSearchResultsCarousel from '@rover/react-lib/src/pages/landing/components/LandingSearchResultsCarousel';
import ServiceBenefits from '@rover/react-lib/src/pages/landing/ServiceLanding/components/ServiceBenefits';
import ServiceLandingServicesWrapper from '@rover/react-lib/src/pages/landing/ServiceLanding/components/ServiceLandingServicesWrapper';
import Props from '@rover/react-lib/src/pages/landing/ServiceLanding/pages/ServiceLandingPageProps';
import AppStoreCallout from '@rover/react-lib/src/pages/shared/AppStoreCallout/AppStoreCallout';
import {
  ROVER_DATA_CITATION,
  Superscript,
} from '@rover/react-lib/src/pages/shared/FooterLinks/FactCitations';
import Footer from '@rover/react-lib/src/pages/shared/FooterLinks/Footer';
import {
  SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM,
  ServiceLandingEnum,
} from '@rover/types/src/ServiceLanding';

import LandingHelmet from '../../../components/LandingHelmet';
import { RoverProtectCondensed } from '../../../SeoLocation/components';
import QuickFact, { QuickFactHighlight } from '../../../SeoLocation/components/QuickFact';
import { getSearchFiltersFromSearchResults } from '../../../utilities';
import servicesLinksImage from '../../components/images/dog-looking-upward.png';
import DogBoardingServiceFaqs from '../../components/ServiceFaqs/DogBoardingServiceFaqs';
import ServiceLandingSection, {
  SERVICE_LANDING_SECTION_MARGIN_Y,
} from '../../components/ServiceLandingSection';
import { SERVICE_SEARCH_FORM_SUBMIT_TYPES } from '../../constants';
import { quickFactStickerChihuahua, quickFactStickerPug } from '../shared-images';

import dogBoardingHeroDesktopImage from './images/dog-boarding-hero-desktop.jpg';
import dogBoardingHeroTabletImage from './images/dog-boarding-hero-tablet.jpg';

const heroImages: HeroImageSrcSet = {
  desktop: {
    src: dogBoardingHeroDesktopImage,
    height: 500,
  },
  tablet: {
    src: dogBoardingHeroTabletImage,
    height: 500,
  },
};

const DogBoarding: React.FC<Props> = ({
  countryConfig,
  language,
  serviceLandingData,
  serviceLinksWithDisplayName,
  searchResults,
  serviceTypes,
  citiesAndNeighborhoodsLinks,
  countryFooterLinks,
  countryRegionLinks,
}) => {
  const serviceLandingType = ServiceLandingEnum.DOG_BOARDING;
  const serviceTypeSlug = SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM[serviceLandingType];

  const { h1, h2, seoTitle, seoMetaDescription } = serviceLandingData;

  const initialSearchFilters = getSearchFiltersFromSearchResults(serviceLandingType, searchResults);

  return (
    <>
      <LandingHelmet title={seoTitle} metaDescription={seoMetaDescription} />
      <LandingHero mb="0x" heroImageSrcSet={heroImages} upliftedContent>
        <LandingHero.Content upliftedContent>
          <LandingHero.Display>{h1}</LandingHero.Display>
          <LandingHero.Text>{h2}</LandingHero.Text>
          <LandingHero.SearchForm
            language={language}
            serviceTypes={serviceTypes}
            serviceTypeSlug={serviceTypeSlug}
            initialSearchFilters={initialSearchFilters}
            showSearchFilters={false}
            submitButtonTextVariant={SERVICE_SEARCH_FORM_SUBMIT_TYPES.DOG_BOARDING}
          />
        </LandingHero.Content>
      </LandingHero>

      {/* Includes Section within component */}
      <RoverProtectCondensed countryConfig={countryConfig} mb={SERVICE_LANDING_SECTION_MARGIN_Y} />

      {/* Includes Section & margins within component */}
      <ServiceBenefits title={<Trans>Your dog’s home away from home</Trans>}>
        <ServiceBenefits.List>
          <ServiceBenefits.ListItem title={<Trans>Loving in-home dog boarding</Trans>}>
            <Trans>
              Keep your dog’s staycation local by booking with a dog sitter in your neighborhood.
              They’ll be in a comfortable home environment and can enjoy familiar routines.
            </Trans>
          </ServiceBenefits.ListItem>
          <ServiceBenefits.ListItem title={<Trans>Dog sitters you can trust</Trans>}>
            <Trans>
              We make it easy to find 5-star, background-checked sitters. Check out sitter profiles
              and read verified reviews to find a sitter your dog will look forward to staying with.
            </Trans>
          </ServiceBenefits.ListItem>
          <ServiceBenefits.ListItem title={<Trans>Get photos & updates</Trans>}>
            <Trans>
              Find out how your dog is doing by requesting updates in the Rover app. Get photos from
              your sitter of everything from playtime to nap time.
            </Trans>
          </ServiceBenefits.ListItem>
        </ServiceBenefits.List>
      </ServiceBenefits>

      {searchResults && searchResults.results?.length > 0 && (
        <ServiceLandingSection>
          <LandingSearchResultsCarousel
            searchResponse={searchResults}
            searchFilters={initialSearchFilters}
            language={language}
            heading={t`Highly-rated boarding sitters in your area`}
            headingVariant="display"
            serviceTypeSlug={serviceTypeSlug}
            countryConfig={countryConfig}
          />
        </ServiceLandingSection>
      )}

      {/* Includes Section & margins within component */}
      <QuickFact
        my={SERVICE_LANDING_SECTION_MARGIN_Y}
        themeColor="yellow"
        stickerImageSrc={quickFactStickerChihuahua}
      >
        <Trans>
          Over <QuickFactHighlight>4.1 million</QuickFactHighlight> dogs curled up for a sleepover
          in a sitter's home on Rover
        </Trans>
        <Superscript>1</Superscript>
      </QuickFact>

      <ServiceLandingSection>
        <DogBoardingServiceFaqs />
      </ServiceLandingSection>

      {/* Includes Section & margins within component */}
      <QuickFact
        my={SERVICE_LANDING_SECTION_MARGIN_Y}
        themeColor="pink"
        stickerImageSrc={quickFactStickerPug}
      >
        <Trans>
          Home-like environments are more likely to <QuickFactHighlight>improve</QuickFactHighlight>{' '}
          a dog's well-being than traditional kennels
        </Trans>
        <Superscript>2</Superscript>
      </QuickFact>

      <HowBrandWorks>
        <HowBrandWorks.Display>
          <Trans>How to book dog boarding</Trans>
        </HowBrandWorks.Display>
        <HowBrandWorks.List>
          <HowBrandWorks.ListItem>
            <HowBrandWorks.ItemHeading step={1}>
              <Trans>Search</Trans>
            </HowBrandWorks.ItemHeading>
            <HowBrandWorks.ItemContent>
              <Trans>Browse profiles of sitters near you who offer overnight dog boarding.</Trans>
            </HowBrandWorks.ItemContent>
          </HowBrandWorks.ListItem>
          <HowBrandWorks.ListItem>
            <HowBrandWorks.ItemHeading step={2}>
              <Trans>Meet in person</Trans>
            </HowBrandWorks.ItemHeading>
            <HowBrandWorks.ItemContent>
              <Trans>Schedule a free in-person Meet & Greet to find the perfect fit.</Trans>
            </HowBrandWorks.ItemContent>
          </HowBrandWorks.ListItem>
          <HowBrandWorks.ListItem>
            <HowBrandWorks.ItemHeading step={3}>
              <Trans>Book &amp; pay</Trans>
            </HowBrandWorks.ItemHeading>
            <HowBrandWorks.ItemContent>
              <Trans>
                It's that easy! All payments are processed through Rover's secured platform.
              </Trans>
            </HowBrandWorks.ItemContent>
          </HowBrandWorks.ListItem>
        </HowBrandWorks.List>
      </HowBrandWorks>

      {/* Includes Section & margins within component */}
      <ServiceLandingServicesWrapper
        serviceLandingType={serviceLandingType}
        serviceLinksWithDisplayName={serviceLinksWithDisplayName}
        imageSrc={servicesLinksImage}
      >
        <Trans>Rover offers many services to meet your pet care needs, including:</Trans>
      </ServiceLandingServicesWrapper>

      <ServiceLandingSection mb={['0x', '0x']} data-qa-id="service-landing-download-app-callout">
        <AppStoreCallout />
      </ServiceLandingSection>

      <Footer
        citiesAndNeighborhoodsLinks={citiesAndNeighborhoodsLinks}
        countryFooterLinks={countryFooterLinks}
        countryRegionLinks={countryRegionLinks}
        citations={[
          ROVER_DATA_CITATION(),
          <>
            Taylor, K. D., et al. (2007).{' '}
            <cite>
              The effect of the kennel environment on canine welfare: A critical review of
              experimental studies.
            </cite>{' '}
            Animal Welfare, 16, 435–447.
          </>,
        ]}
      />
    </>
  );
};

export default DogBoarding;
